import { useMemo } from "react";
import {
  Badge,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type {
  LegacyReportDraftData,
  LegacyReportEvidenceBlock,
} from "../../../types/legacyReportReview";

interface Props {
  index: number;
  form: UseFormReturnType<LegacyReportDraftData>;
  evidence?: LegacyReportEvidenceBlock;
  readOnly?: boolean;
}

const sectionTypeLabels: Record<string, string> = {
  source_header: "Source header",
  source_footer: "Source footer",
};

export default function LegacyReportSectionEditor({
  index,
  form,
  evidence,
  readOnly = false,
}: Props) {
  const section = form.values.sections[index];
  const sourceSectionType = useMemo(
    () => sectionTypeLabels[section.kind],
    [section.kind]
  );
  const pages = useMemo(
    () =>
      Array.from(
        new Set(evidence?.spans.map((span) => span.pageNumber) || [])
      ).join(", "),
    [evidence]
  );
  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>
            {evidence?.sourceLabel ||
              section.label ||
              `Historical section ${index + 1}`}
          </Text>
          <Badge variant="light">
            Source {pages ? `page ${pages}` : "location unavailable"}
          </Badge>
        </Group>
        {sourceSectionType && (
          <Text size="sm" c="dimmed">
            {sourceSectionType} · Preserved source text
          </Text>
        )}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Literal source evidence
              </Text>
              <Text
                size="sm"
                style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {evidence?.text ||
                  "No text evidence is linked. Review the original and resolve the source warning."}
              </Text>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="sm">
              <TextInput
                label="Section label"
                readOnly={readOnly}
                {...form.getInputProps(`sections.${index}.label`)}
              />
              <Textarea
                label="Reconstructed literal text"
                readOnly={readOnly}
                autosize
                minRows={5}
                maxRows={24}
                {...form.getInputProps(`sections.${index}.text`)}
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </Paper>
  );
}
